import * as React from 'react';
import Form from 'react-bootstrap/Form';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import Collapse from 'react-bootstrap/Collapse';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import { Formik } from 'formik';
import { generateYup } from '../../services/generateYupSchema';
import { useTranslation } from 'react-i18next';
import { submitData } from '../../services/contactForm';
import { submitProject } from '../../services/projects';
import * as styles from '../../styles/form.module.scss';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import ReactMarkdown from 'react-markdown';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ComfortIcons from '../ComfortIcons';

function UserForm({ type, form, onQuoteFormStarted }) {
  const { t } = useTranslation(['validation', 'quoteForm']);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [question, setQuestion] = React.useState(0);
  const [quoteFormDone, setQuoteFormDone] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const formSectionsRef = React.useRef([]);
  const initialValues = {};
  const hiddenFields = form?.form.filter(
    (field) => field.input.type.toLowerCase() === 'hidden',
  ).length;
  const result = useStaticQuery(
    graphql`
      {
        cms {
          pages(where: { type: "terms" }) {
            title
            content
            sites {
              url
            }
          }
        }
      }
    `,
  );
  const userTerms = result.cms.pages.filter((page) =>
    page.sites.some((site) => site.url === process.env.GATSBY_FRONTEND_DOMAIN),
  )[0];
  form?.form.forEach(
    (field) => (initialValues[field.input.name] = field.input.value || ''),
  );

  React.useEffect(() => {
    const handleNextStep = () => {
      formSectionsRef.current[question].style.display = 'block';
      setProgress(
        progress +
          Math.round(
            100 /
              (formSectionsRef.current.filter(
                (element) => element.localName === 'fieldset',
              ).length +
                1),
          ),
      );
      document.body.scrollTo({ top: 0, behavior: 'smooth' });

      //Hide form inputs which is not inside a fieldset (like contact form)
      if (question === 0) {
        formSectionsRef.current.forEach((element, index) => {
          if (element.localName !== 'fieldset') {
            formSectionsRef.current[index].style.display = 'none';
          }
        });
      }

      if (question === 1) onQuoteFormStarted(true);

      //Hide previous section
      if (
        question > 0 &&
        question <=
          formSectionsRef.current.filter(
            (element) => element.localName === 'fieldset',
          ).length
      ) {
        formSectionsRef.current[question - 1].style.display = 'none';
      }

      if (
        question ===
        formSectionsRef.current.filter(
          (element) => element.localName === 'fieldset',
        ).length
      ) {
        setQuoteFormDone(true);
        setProgress(100);

        formSectionsRef.current.forEach((element, index) => {
          if (element.localName !== 'fieldset') {
            formSectionsRef.current[index].style.display = 'block';
          }
        });
      }
    };

    handleNextStep();
  }, [question]);

  async function handleSubmit(values, { props, setErrors }) {
    setSubmitting(true);

    const recaptchaToken = await executeRecaptcha(type);
    values['recaptcha'] = recaptchaToken;
    values['sitedomain'] =
      window && window.location.hostname ? window.location.hostname : '';

    if (type === 'contact') {
      submitData(values)
        .then(() => setSubmitted(true))
        .catch(() => setShowErrorModal(true))
        .finally(() => setSubmitting(false));
    } else {
      submitProject(
        values,
        sessionStorage.getItem('gclid') || '',
        sessionStorage.getItem('msclkid') || '',
      )
        .then((res) => {
          if (res?.data['returnData']['_text'] === 'OK') {
            setSubmitted(true);
            document.body.scrollTo({ top: 0, behavior: 'smooth' });

            typeof window !== 'undefined' &&
              window.gtag('event', 'quoteform_submitted', {
                send_to: process.env.GATSBY_GA_TRACKING_ID,
              });
            setTimeout(() => {
              navigate('/');
              onQuoteFormStarted(false);
            }, 15000);
            return;
          }

          if (res?.data['errors']?.['error']?.['code']?.['_text'] === '420') {
            setErrors({
              postcode: t('invalid_postcode', { ns: 'validation' }),
            });
          } else {
            setShowErrorModal(true);
          }
        })
        .catch(() => setShowErrorModal(true))
        .finally(() => setSubmitting(false));
    }
  }

  return (
    <>
      <div className={`card col-span-2 grid-item-center ${styles.form}`}>
        {submitting ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          ''
        )}
        <div style={{ display: submitting || submitted ? 'none' : 'block' }}>
          <Formik
            validateOnBlur
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={generateYup(form?.form) || {}}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              setFieldTouched,
              handleBlur,
            }) => (
              <Form
                noValidate
                onSubmit={handleSubmit}
                method="POST"
                className={type === 'quote_form' ? 'quote_form' : ''}
              >
                {form?.form.map((field, index) => {
                  return field.input.type !== 'hidden' ? (
                    <FormFieldWrapper
                      field={field}
                      index={index}
                      key={`wrapper${index}`}
                      ref={(element) =>
                        element && question === 0 && progress === 0
                          ? formSectionsRef.current.push(element)
                          : ''
                      }
                    >
                      <FormField
                        field={field}
                        index={index}
                        key={`${index}${index}`}
                        handleChange={handleChange}
                        errors={errors}
                        values={values}
                        touched={touched}
                        handleBlur={handleBlur}
                      />
                    </FormFieldWrapper>
                  ) : (
                    <FormField
                      field={field}
                      index={index}
                      key={`${index}${index}`}
                      handleChange={handleChange}
                      errors={errors}
                      values={values}
                      touched={touched}
                      handleBlur={handleBlur}
                    />
                  );
                })}
                {type === 'quote_form' ? (
                  <>
                    {quoteFormDone ? (
                      <>
                        <p>
                          <Button
                            variant="link"
                            onClick={() => setShowTermsModal(true)}
                          >
                            {userTerms.title}
                          </Button>{' '}
                          – {t('user_terms_label', { ns: 'quoteForm' })}
                        </p>
                        <button type="submit" className="primary block">
                          {t('done_button', { ns: 'quoteForm' })}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          setFieldTouched(
                            Object.keys(values)[hiddenFields + question],
                            true,
                            true,
                          ).then((err) =>
                            !err.hasOwnProperty(
                              Object.keys(values)[hiddenFields + question],
                            )
                              ? setQuestion(question + 1)
                              : '',
                          )
                        }
                        className="primary block"
                      >
                        {t('next_button', { ns: 'quoteForm' })}
                      </button>
                    )}
                    <ProgressBar now={progress} />
                    <span>{progress}%</span>
                  </>
                ) : (
                  ''
                )}
                <hr />
                <ComfortIcons list={form.comfort} fontColor="000" />
                {type === 'contact' ? (
                  <button type="submit" className="primary">
                    {t('contact_me_button', { ns: 'quoteForm' })}
                  </button>
                ) : (
                  ''
                )}
              </Form>
            )}
          </Formik>
        </div>

        {submitted ? (
          type === 'contact' ? (
            <>
              <h2>{t('message_sent_title', { ns: 'quoteForm' })}</h2>
              <p>{t('message_sent_description', { ns: 'quoteForm' })}</p>
            </>
          ) : (
            <>
              <h2>{t('quote_form_sent_title', { ns: 'quoteForm' })}</h2>
              <p>{t('quote_form_sent_description', { ns: 'quoteForm' })}</p>
            </>
          )
        ) : (
          ''
        )}
      </div>

      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        scrollable={true}
        size="lg"
      >
        <Modal.Header closeButton closeVariant="black">
          <Modal.Title>
            <h4>{userTerms.title}</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReactMarkdown>
            {userTerms.content.replaceAll(
              '%siteurl%',
              process.env.GATSBY_FRONTEND_DOMAIN.charAt(0).toUpperCase() +
                process.env.GATSBY_FRONTEND_DOMAIN.slice(1) || '',
            )}
          </ReactMarkdown>
        </Modal.Body>
        <Modal.Footer>
          <button className="primary" onClick={() => setShowTermsModal(false)}>
            {t('accept_terms', { ns: 'quoteForm' })}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h4>{t('error.title', { ns: 'quoteForm' })}</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('error.description', { ns: 'quoteForm' })}</p>
        </Modal.Body>
        <Modal.Footer>
          <button className="primary" onClick={() => setShowErrorModal(false)}>
            {t('error.action', { ns: 'quoteForm' })}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

const FormFieldWrapper = React.forwardRef(({ field, index, children }, ref) => {
  return field.input.single ? (
    <fieldset ref={ref}>
      {field.input.heading ? <h2>{field.input.heading}</h2> : ''}
      <Form.Group key={`${field.input.name}${index}`} className="mb-4">
        <Form.Label key={field.input.name} htmlFor={field.input.name}>
          {field.input.label}
        </Form.Label>
        {children}
      </Form.Group>
    </fieldset>
  ) : (
    <Form.Group ref={ref} key={`${field.input.name}${index}`} className="mb-4">
      {<h2>{field.input.heading}</h2> || ''}
      <Form.Label key={field.input.name} htmlFor={field.input.name}>
        {field.input.label}
      </Form.Label>
      {children}
    </Form.Group>
  );
});

function FormField({
  field,
  index,
  handleChange,
  values,
  touched,
  handleBlur,
  errors,
}) {
  switch (field.input.type) {
    case 'text':
    case 'tel':
    case 'date':
    case 'email':
    case 'number':
      return (
        <>
          <Form.Control
            isValid={!errors[field.input.name] && touched[field.input.name]}
            isInvalid={errors[field.input.name] && touched[field.input.name]}
            type={field.input.type}
            onChange={handleChange}
            onBlur={handleBlur}
            key={`${field.input.name}${index}${index}`}
            required={field.input.required}
            name={field.input.name}
            id={field.input.name}
            value={values[field.input.name]}
            placeholder={field.input.placeholder}
          />
          <FieldFeedback field={field} errors={errors} />
        </>
      );
    case 'hidden':
      return (
        <Form.Control
          type={field.input.type}
          key={`${field.input.name}${index}${index}`}
          name={field.input.name}
          value={values[field.input.name]}
        />
      );
    case 'textarea':
      return (
        <>
          <Form.Control
            as="textarea"
            rows={4}
            isValid={!errors[field.input.name] && touched[field.input.name]}
            isInvalid={errors[field.input.name] && touched[field.input.name]}
            onBlur={handleBlur}
            key={`${field.input.name}${index}${index}`}
            required={field.input.required}
            name={field.input.name}
            onChange={handleChange}
            id={field.input.name}
            value={values[field.input.name]}
            placeholder={field.input.placeholder}
          />
          <FieldFeedback field={field} errors={errors} />
        </>
      );
    case 'select':
      return (
        <>
          <SelectElement
            field={field}
            errors={errors}
            touched={touched}
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            key={`${field.input.name}${index}${index}`}
          />
          <FieldFeedback field={field} errors={errors} />
        </>
      );
    case 'styled_button':
      return (
        <RadioBtnSelect
          field={field}
          errors={errors}
          touched={touched}
          values={values}
          handleChange={handleChange}
          handleBlur={handleBlur}
          key={`${field.input.name}${index}${index}${index}`}
        />
      );
    default:
      return true;
  }
}

function FieldFeedback({ field, errors, className }) {
  return (
    <Form.Control.Feedback type="invalid" className={className}>
      {errors[field.input.name]}
    </Form.Control.Feedback>
  );
}

function SelectElement({
  field,
  values,
  touched,
  handleChange,
  handleBlur,
  errors,
}) {
  return (
    <Form.Select
      name={field.input.name}
      id={field.input.name}
      isValid={!errors[field.input.name] && touched[field.input.name]}
      isInvalid={errors[field.input.name] && touched[field.input.name]}
      value={values[field.input.name]}
      onChange={handleChange}
      onBlur={handleBlur}
    >
      {field.options.map((option, i) => (
        <option value={option.value} key={`${field.input.name}${i}`}>
          {option.option}
        </option>
      ))}
    </Form.Select>
  );
}

function RadioBtnSelect({
  field,
  values,
  touched,
  handleChange,
  handleBlur,
  errors,
}) {
  const [radioButtons, setRadioButtons] = React.useState([]);
  const [hiddenRadioButtons, setHiddenRadioButtons] = React.useState([]);

  React.useEffect(() => {
    if (field.options && field.options.length > 5) {
      setRadioButtons(field.options.slice(0, 6));
      setHiddenRadioButtons(field.options.slice(6, field.options.length));
    } else {
      setRadioButtons(field.options);
    }
  }, []);

  return (
    <ToggleButtonGroup type="radio" name={field.input.name} onBlur={handleBlur}>
      {radioButtons &&
        radioButtons.length > 0 &&
        radioButtons.map((option, i) => (
          <ToggleButton
            key={`${field.input.name}${i}`}
            className="block"
            id={`${field.input.name}${i}`}
            value={option.value}
            onChange={handleChange}
          >
            {option.option}
          </ToggleButton>
        ))}

      {hiddenRadioButtons && hiddenRadioButtons.length > 0 ? (
        <ShowMore>
          <ToggleButtonGroup
            type="radio"
            name={field.input.name}
            onBlur={handleBlur}
          >
            {hiddenRadioButtons.map((option, i) => (
              <ToggleButton
                key={`${field.input.name}${i}${i}`}
                className="block"
                id={`${field.input.name}${i}${i}`}
                value={option.value}
                onChange={handleChange}
              >
                {option.option}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </ShowMore>
      ) : null}

      <FieldFeedback field={field} errors={errors} className="d-block" />
    </ToggleButtonGroup>
  );
}

function ShowMore({ children }) {
  const { t } = useTranslation(['validation', 'quoteForm']);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {children ? (
        <Collapse in={open}>
          <div>{children}</div>
        </Collapse>
      ) : null}
      <div className="center">
        <button
          type="button"
          className="secondary"
          onClick={() => setOpen(!open)}
        >
          {open
            ? t('show_less_button', { ns: 'quoteForm' })
            : t('show_more_button', { ns: 'quoteForm' })}
        </button>
      </div>
    </>
  );
}

export default UserForm;
